/**
 * The mark beside a row (D103, D334).
 *
 * Three states, and the first is nothing. AT REST a row wears no mark — only
 * the empty slot one would sit in, so a name never moves when a mark comes.
 * A HOLLOW RING while something is arriving in that exploration: its run, or a
 * chat answer still streaming after the reader left it. A FILLED DOT once a
 * run finished there, or an answer landed while the reader was elsewhere, and
 * the exploration has not been opened here since.
 *
 * The filled dot says one thing: something finished in this exploration, and
 * you have not opened it since. Not "a run has finished here" — that was the
 * first cut, a fact about the exploration that never cleared — but a mark
 * the act of opening takes down, the way the reference's dot clears when a
 * session is viewed. It replaced a ring that sat on every row at rest (D103)
 * and lit blue when unread; a mark on every row said "this is a row", which
 * the row already says, and left the one state worth a colour to a hairline.
 *
 * WHAT IS RECORDED IS WHEN EACH EXPLORATION WAS LAST OPENED, per device, and
 * nothing about the runs. A run's finish already carries its own time — the
 * index row's `finishedRunAt` — so "unread" is one comparison: the latest run
 * finished after the exploration was last opened. Three acts count as
 * opening: switching to an exploration (every door the app has goes through
 * the autosave's one session-switch hook); pressing the row of the one that is
 * already open, in the sidebar or on the search sheet — which is how a filled
 * dot is put out without leaving; and starting a run in it. A run only ever
 * runs in the open exploration, so whoever started it is looking at it, and
 * that stamp is what the run's finish is later measured against.
 *
 * NEVER OPENED READS AS READ. An exploration with no stamp has not been opened
 * since this mark existed, so any run in it finished before that — on an
 * earlier build, or on the device it was shared from — and lighting every such
 * row on the first load after an upgrade would be a list saying "new" about
 * things that are not. The first run started in it stamps it, and that run's
 * finish is what fills its dot for the first time.
 *
 * THE RING NEEDS NO RECORD. A run only ever runs in the open exploration, so
 * "a run is going in X" is exactly "X is open and the store's run is live" —
 * the live store says it, and the index is never asked. Single passes (Deepen
 * and the rest) are left out on purpose: they write no run and no finish, so
 * a ring for one would go out with no dot after it, and the canvas already
 * shows them in the seconds they last.
 *
 * AN ANSWER LANDING AWAY IS MEMORY ONLY. A chat answer keeps arriving after the
 * reader opens another exploration (`ui/askLive.ts`), and where it landed is
 * the one observable "you were not looking". It fills the dot only when it
 * lands away from the open exploration — an answer streaming into the thread
 * the reader just typed in is being watched — and the time it landed is never
 * written down: a conversation belongs to its reader (D330) and `nv.prefs` is
 * the device's, so a stored stamp would tell the next reader on this device
 * that somebody had a conversation there. A reload forgets that dot; a run's
 * survives, because it rides the index.
 *
 * TASTE, so it rides in `nv.prefs` with the folded projects (D101) and for
 * their reason: the worst a lost write does is show a dot filled again. Not a
 * fact about the document — an exploration shared to a colleague must not
 * arrive "already read" because you had read it — and not the pins' slot
 * either, because nothing has to take a stamp down when its exploration is
 * forgotten: a stamp for a gone id matches no row and draws nothing, and the
 * cap below keeps the map from growing without bound.
 *
 * Pure, and it imports nothing. The store owns the map and is its only writer;
 * `persist.ts` decodes it off the blob.
 */

/** When each exploration was last opened on this device, by id, in epoch
 *  milliseconds. */
export type OpenedMap = Readonly<Record<string, number>>

/**
 * How many explorations one device remembers opening.
 *
 * A bound on a runaway, not a budget anybody is meant to feel: past it the
 * OLDEST stamp falls off, and an exploration whose stamp fell off reads as
 * never opened, which is the quiet state. Well past the library sizes this
 * app sees, for one line in a blob that is parsed once per boot.
 */
export const MAX_OPENED = 400

/**
 * The map as it comes off the prefs blob — already-parsed JSON, so this takes
 * the value and not the bytes. Anything but an object reads as nothing
 * opened; only non-empty ids with a finite, positive time survive; and the
 * cap keeps the newest.
 */
export function parseOpened(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, number> = {}
  for (const [id, at] of Object.entries(raw as Record<string, unknown>)) {
    if (!id || typeof at !== 'number' || !Number.isFinite(at) || at <= 0) continue
    out[id] = at
  }
  return capped(out)
}

/**
 * Stamp one exploration as opened at `at`. A new object every time, because
 * the prefs autosave compares by identity (`prefsChanged`); null for an empty
 * id or a time that is not one, so the store writes nothing.
 */
export function withOpened(
  opened: OpenedMap,
  id: string,
  at: number,
): Record<string, number> | null {
  if (!id || !Number.isFinite(at) || at <= 0) return null
  return capped({ ...opened, [id]: at })
}

/** The newest `MAX_OPENED` stamps, when there are more. */
function capped(opened: Record<string, number>): Record<string, number> {
  const ids = Object.keys(opened)
  if (ids.length <= MAX_OPENED) return opened
  const keep = ids.sort((a, b) => opened[b]! - opened[a]!).slice(0, MAX_OPENED)
  return Object.fromEntries(keep.map((id) => [id, opened[id]!]))
}

/**
 * Whether a row's run is unread: its latest run finished after the exploration
 * was last opened here. The run half of `rowMark`, kept on its own because the
 * store's own tests measure a run's finish against the stamp its start wrote.
 */
export function unreadRun(row: { id: string; finishedRunAt?: number }, opened: OpenedMap): boolean {
  const at = opened[row.id]
  return row.finishedRunAt !== undefined && at !== undefined && row.finishedRunAt > at
}

/** Which mark a row wears; none at rest. */
export type RowMark = 'running' | 'unread'

/** What the live app knows that the index does not — read off the store and the
 *  conversations by `ui/rowMarks.ts`, and handed in so this stays pure. */
export interface LiveMarks {
  /** Explorations something is arriving in now: the open one while its run is
   *  live, and any one a chat answer is still arriving in after the reader
   *  left it. */
  running: readonly string[]
  /** When an answer landed in an exploration the reader was NOT in, by id.
   *  Memory only — see the header. */
  answeredAt: OpenedMap
}

/** Nothing live: the index's half alone — for a surface rendered without a
 *  store behind it. */
export const NO_LIVE_MARKS: LiveMarks = { running: [], answeredAt: {} }

/**
 * The mark beside a row (D334). One rule for every surface that draws one, so
 * the sidebar's two copies of a row and the search sheet cannot disagree.
 *
 * The ring wins over the dot: something arriving now is the newer news, and
 * the two meet only when an answer streams into an exploration whose earlier
 * finish is still unread — starting a run stamps the exploration as opened.
 * Never opened reads as read for an answer exactly as for a run.
 */
export function rowMark(
  row: { id: string; finishedRunAt?: number },
  opened: OpenedMap,
  live: LiveMarks,
): RowMark | undefined {
  if (live.running.includes(row.id)) return 'running'
  const at = opened[row.id]
  if (at === undefined) return undefined
  const answered = live.answeredAt[row.id]
  return unreadRun(row, opened) || (answered !== undefined && answered > at) ? 'unread' : undefined
}

/**
 * The one mark a FOLDED list wears — on the collapsed rail's expand button and
 * the phone's opener, where no row is on screen to wear its own (D334). The dot
 * before the ring: an unread exploration is the one that asks for a press, and
 * a ring resolves itself into a dot anyway. `unread` counts, so the opener can
 * say how many.
 */
export function foldedMark(
  rows: readonly { id: string; finishedRunAt?: number }[],
  opened: OpenedMap,
  live: LiveMarks,
): { mark: RowMark; unread: number } | undefined {
  let unread = 0
  let running = false
  for (const r of rows) {
    const m = rowMark(r, opened, live)
    if (m === 'unread') unread++
    else if (m === 'running') running = true
  }
  if (unread > 0) return { mark: 'unread', unread }
  return running ? { mark: 'running', unread: 0 } : undefined
}
